// src/utils/constants.ts
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  SIGNUP: '/signup',
  VERIFY_OTP: '/verify-otp',
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  HOST_MEETING: '/host-meeting',
  JOIN_MEETING: '/join-meeting',
  MEETING_PASSES: '/meeting-passes',
  PAYMENT_CALLBACK: '/subscriptions/payment-callback',
  SETTINGS: '/settings',
  HELP: '/help',
  NOT_FOUND: '*',
  // Add the new USE_CASES route
  USE_CASES: '/use-cases',
  // Add the new SERVICE_PROVIDERS route
  SERVICE_PROVIDERS: '/service-providers',
  // Legal pages routes
  TERMS_OF_SERVICE: '/terms-of-service',
  PRIVACY_POLICY: '/privacy-policy',
  // Admin routes
  ADMIN: '/admin',
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_MEETINGS: '/admin/meetings',
  ADMIN_MEETING_DETAIL: '/admin/meetings/:id',
  ADMIN_USERS: '/admin/users',
  ADMIN_USER_DETAIL: '/admin/users/:id',
  ADMIN_REPORTS: '/admin/reports',
  ADMIN_REPORT_DETAIL: '/admin/reports/:id',
  // Meeting link routes
  MEETING_LINK: '/m/:meetingId',
  // Public meeting route
  PUBLIC_MEETING: '/p/:meetingId',
  PUBLIC_MEETING_BASE: '/p',
  // Reports routes
  REPORTS: '/reports',
  REPORTS_WITH_TAB: '/reports?tab=:tab'
};

export const SUBSCRIPTION_TIERS = {
  FREE: 'free',
  VELOCITY: 'velocity',
  GROWTH: 'growth',
  ENTERPRISE: 'enterprise',
  ENTERPRISE_PRO: 'enterprise_pro'
};

// Features by tier
export const TIER_FEATURES = {
  [SUBSCRIPTION_TIERS.FREE]: {
    passes: 2,
    pricePerPass: 'Free',
    validity: 30,
    queuePriority: 5,
    rollover: false,
    whatsapp: false
  },
  [SUBSCRIPTION_TIERS.VELOCITY]: {
    passes: 6,
    pricePerPass: '₹500',
    validity: 30,
    queuePriority: 4,
    rollover: true,
    whatsapp: false
  },
  [SUBSCRIPTION_TIERS.GROWTH]: {
    passes: 12,
    pricePerPass: '₹417',
    validity: 45,
    queuePriority: 3,
    rollover: true,
    whatsapp: false
  },
  [SUBSCRIPTION_TIERS.ENTERPRISE]: {
    passes: 25,
    pricePerPass: '₹400',
    validity: 60,
    queuePriority: 2,
    rollover: true,
    whatsapp: false
  },
  [SUBSCRIPTION_TIERS.ENTERPRISE_PRO]: {
    passes: 45,
    pricePerPass: '₹444',
    validity: 90,
    queuePriority: 1,
    rollover: true,
    whatsapp: true
  }
};

export const USER_ROLES = {
  USER: 'user',
  ADMIN: 'admin'
};

export const MEETING_TYPES = {
  ONE_ON_ONE: 'one_on_one',
  GROUP: 'group',
  WEBINAR: 'webinar'
};

export const MEETING_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ON_HOLD: 'on-hold',
  CANCELLED: 'cancelled',
  ALL: 'all'
};

// Waiting room constants
export const WAITING_ROOM_STATUS = {
  NOT_STARTED: 'not_started',
  OPEN: 'open',
  HOST_JOINED: 'host_joined',
  HOST_AWAY: 'host_away',
  MEETING_STARTED: 'meeting_started',
  CLOSED: 'closed'
};

export const HOST_STATUS = {
  PRESENT: 'present',
  AWAY: 'away',
  LEFT: 'left'
};

export const MEETING_LINK_TIMING = {
  EARLY_THRESHOLD: 10 * 60 * 1000, // 10 minutes in ms (changed from 15)
  WAITING_ROOM_THRESHOLD: 30 * 60 * 1000, // 30 minutes in ms
  HOST_GRACE_PERIOD: 30 * 1000, // 30 seconds in ms
  RECONNECT_GRACE_PERIOD: 5 * 1000 // 5 seconds in ms
};

// API Base URL and socket URL
export const API_BASE_URL = 'https://testspeedmeetbackend.onrender.com';
export const SOCKET_URL = 'https://testspeedmeetbackend.onrender.com';
export const TOKEN_KEY = 'speedmeet_token';
export const REFRESH_TOKEN_KEY = 'speedmeet_refresh_token';
export const USER_KEY = 'speedmeet_user';

// Daily.co constants
export const DAILY_DOMAIN = 'testspeedmeet.daily.co';

// Admin-specific constants
export const ADMIN_MEETING_TABS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ALL: 'all'
};

export const ADMIN_SOCKET_EVENTS = {
  MEETING_CREATED: 'admin:meeting-created',
  MEETING_UPDATED: 'admin:meeting-updated',
  MEETING_APPROVED: 'meeting:approved',
  MEETING_REJECTED: 'meeting:rejected',
  USER_ROLE_CHANGED: 'user:role-changed',
  USER_STATUS_CHANGED: 'user:status-changed',
  REPORT_UPDATED: 'admin:report-updated'
};

// Waiting room socket events
export const WAITING_ROOM_EVENTS = {
  // Client to Server
  JOIN_QUEUE: 'waitingRoom:join',
  LEAVE_QUEUE: 'waitingRoom:leave',
  HEARTBEAT: 'waitingRoom:heartbeat',
  GET_POSITION: 'waitingRoom:getPosition',
  HOST_JOIN: 'meeting:hostJoin',
  HOST_ADMIT_NEXT: 'meeting:admitNext',
  HOST_ADMIT_USER: 'meeting:admitUser',
  HOST_LEAVE: 'meeting:hostLeave',
  HOST_HEARTBEAT: 'waitingRoom:hostHeartbeat',
  
  // Server to Client
  POSITION_UPDATE: 'waitingRoom:positionUpdate',
  BATCH_POSITION_UPDATE: 'waitingRoom:batchUpdate',
  MEETING_UPDATE: 'meeting:update',
  ADMITTED: 'waitingRoom:admitted',
  HOST_STATUS: 'meeting:hostStatus',
  HOST_AWAY: 'meeting:hostAway',
  QUEUE_UPDATE: 'waitingRoom:queueUpdate',
  QUEUE_INFO: 'waitingRoom:queueInfo',
  ERROR: 'waitingRoom:error'
};

// Public meeting constants
export const PUBLIC_MEETING_STATUS = {
  UPCOMING: 'upcoming',
  LIVE: 'live',
  STARTING_SOON: 'starting-soon',
  COMPLETED: 'completed',
  ENDED: 'ended',
  CANCELLED: 'cancelled',
  PENDING: 'pending'
};

// Report constants
export const REPORT_TABS = {
  HOSTED: 'hosted',
  JOINED: 'joined'
};

// Report socket events
export const REPORT_SOCKET_EVENTS = {
  CACHE_UPDATED: 'report:cache-updated',
  MEETING_UPDATED: 'report:meeting-updated'
};

// Subscription events
export const SUBSCRIPTION_EVENTS = {
  // Client to server
  GET_ACTIVE: 'subscription:get-active',
  GET_ALL: 'subscription:get-all',
  SET_ACTIVE: 'subscription:set-active',
  CHECK_UPDATE: 'subscription:check-update',
  CHECK_PASSES_UPDATE: 'meetingPasses:check-update',
  
  // Server to client
  ACTIVE: 'subscription:active',
  LIST: 'subscription:list',
  ACTIVE_CHANGED: 'subscription:active-changed',
  UPDATED: 'subscription:updated',
  PURCHASED: 'subscription:purchased',
  EXPIRED: 'subscription:expired',
  PASSES_UPDATED: 'meetingPasses:updated'
};
